import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import TimeSeriesGraph from "../timeSeriesGraph/index";
// Labels moved to i18n; use translation keys instead of mgaLabels
import MGAAPI from "../../api/index";
import displayError from "../../constants/utils";
import { Grid, Select, Text, TextInput } from "@mantine/core";
import { IconCaretDownFilled } from "@tabler/icons-react";
import PropTypes from "prop-types";
import _ from "lodash";
import { sanitizeDigits } from "../../utils/helpers";
import { capitalizeFirstLetterAndSplitName } from "../../constants/helper";
import { useAtom } from "jotai";
import { saveDisabledAtom } from "../../utils/atoms/saveDisabledAtom";
import { helpContextMenuAtom } from "../../utils/atoms/helpContextMenuAtom";
import { helpAtom } from "../../utils/atoms/helpAtom";
import { useHelpFocusBlur } from "../../utils/useHelpFocusBlur";

const MGA_API = new MGAAPI();

const ASHRAELoad = ({
  componentData,
  getBuildingTypes,
  selectedBuildingType,
  displayLoader,
  ashraeData,
  projectDetails,
  setDisplayLoader,
  form,
  updateLoadState,
  loadParameters,
}) => {
  const { t } = useTranslation();
    const [, setSaveDisabled] = useAtom(saveDisabledAtom);
  const [ASHRAEDataAssest, setASHRAEDataAssest] = useState(ashraeData);
  const [initialData, setInitialData] = useState(null);
  const [scalingValue, setScalingValue] = useState(
    loadParameters?.componentAttributes?.loadParameters?.profileDetail?.scalingFactor || 1
  );
    const ashraeContainerRef = useRef(null);
  const [helpMenuData] = useAtom(helpContextMenuAtom);
  const [helperAtom, setHelperAtom] = useAtom(helpAtom);
  

  const getFieldAttributes = (fieldKey) => {
    const field = helpMenuData?.load?.[fieldKey];
    return {
      title: field?.title || "",
      "data-description": field?.description || "",
      // placeholder: field?.placeholder || "",
    };
  };
  useHelpFocusBlur(ashraeContainerRef, setHelperAtom, helperAtom);

  const resetSaveDisabledForAshrae = () => {
    setSaveDisabled(false);
  };

  useEffect(()=>{
    resetSaveDisabledForAshrae();
  },[])

  const [stats, setStats] = useState({
    electrical: { max: 0, min: 0, mean: 0, energy: 0 },
    thermal: { max: 0, min: 0, mean: 0, energy: 0 },
  });
  const [displayUnit, setDisplayUnit] = useState("W");
  // Persist full-year stats once; ignore later zoom-driven updates
  const fullYearStatsSetRef = useRef(false);

  const getAshraeData = (value) => {
    setDisplayLoader(true);
    MGA_API.ashraeBuildingTypeProjectDetails(
      projectDetails.projectDTO.projectId,
      value
    )
      .then((res) => {
        // New building type -> reset full-year stats latch so fresh stats can be captured
        fullYearStatsSetRef.current = false;
        setDisplayUnit("W");
        setStats({
          electrical: { max: 0, min: 0, mean: 0, energy: 0 },
          thermal: { max: 0, min: 0, mean: 0, energy: 0 },
        });
        setASHRAEDataAssest(res);
        setInitialData(res);
        setDisplayLoader(false);
      })
      .catch((res) => {
        setDisplayLoader(false);
        displayError("error", res.response.data);
      });
  };

  const handleChartStats = (chartStats) => {
    // chartStats: { electrical: {min,max,mean,energy}, thermal?: {...} }
    const fmt = (v) => {
      if (v == null || isNaN(v)) return 0;
      return Math.abs(Number(v)).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };
    
    // Update stats only if we have valid data
    if (!chartStats || !chartStats.electrical) return;
    
    // Only set once for the overall year; subsequent zoom changes should not update these displayed values
    setStats((prev) => {
      if (fullYearStatsSetRef.current) {
        return prev;
      }
      
      const next = {
        electrical: {
          max: fmt(chartStats.electrical.max),
          min: fmt(chartStats.electrical.min),
          mean: fmt(chartStats.electrical.mean),
          energy: fmt(chartStats.electrical.energy),
        },
        thermal: chartStats.thermal
          ? {
              max: fmt(chartStats.thermal.max),
              min: fmt(chartStats.thermal.min),
              mean: fmt(chartStats.thermal.mean),
              energy: fmt(chartStats.thermal.energy),
            }
          : prev.thermal,
      };
      
      // Mark as set when we have valid electrical stats
      if (chartStats.electrical && (chartStats.electrical.max !== 0 || chartStats.electrical.energy !== 0)) {
        fullYearStatsSetRef.current = true;
      }
      
      return next;
    });
  };

  const handleUnitChange = (unit) => {
    setDisplayUnit(unit);
    fullYearStatsSetRef.current = false;
    setStats({
      electrical: { max: 0, min: 0, mean: 0, energy: 0 },
      thermal: { max: 0, min: 0, mean: 0, energy: 0 },
    });
  };

  const handleScalingBuildingType = () => {
    // Early return if initialData is not loaded yet
    if (!initialData || !initialData?.ProfileElec?.[1] || !initialData?.ProfileTherm?.[1]) {
      return;
    }

    let updatedBuildingTypeWithScaling = {
      ProfileElec: [[], []],
      ProfileTherm: [[], []],
    };

    const numericScalingValue = parseFloat(scalingValue);

    if (scalingValue && numericScalingValue > 0) {
      updatedBuildingTypeWithScaling = {
        ProfileElec: [
          [],
          _.map(initialData?.ProfileElec[1], (num) => num * numericScalingValue),
        ],
        ProfileTherm: [
          [],
          _.map(initialData?.ProfileTherm[1], (num) => num * numericScalingValue),
        ],
      };
      setASHRAEDataAssest(updatedBuildingTypeWithScaling);
    } else if (scalingValue === "" || numericScalingValue === 0 || isNaN(numericScalingValue)) {
      updatedBuildingTypeWithScaling = {
        ProfileElec: [[], _.map(initialData?.ProfileElec[1], (num) => num * 0)],
        ProfileTherm: [
          [],
          _.map(initialData?.ProfileTherm[1], (num) => num * 0),
        ],
      };
      setASHRAEDataAssest(updatedBuildingTypeWithScaling);
    }
  };

  const buildingTypeDropdownOptions =
    getBuildingTypes && getBuildingTypes?.length > 0
      ? getBuildingTypes?.map((type) => ({
          value: type,
          label: capitalizeFirstLetterAndSplitName(type),
        }))
      : [];

  const defaultbuildingTypeOptions = [
    {
      value: t('ashrae.noBuildingType'),
      label: capitalizeFirstLetterAndSplitName(t('ashrae.noBuildingType')),
      disabled: true,
    },
  ];

  const buildingTypeOptions = [
    ...buildingTypeDropdownOptions,
    ...defaultbuildingTypeOptions,
  ];

  const handleScaleChange = (value) => {
    const numValue = parseFloat(value);
    
    // Only update if value is valid and not empty
    if (value === "" || isNaN(numValue)) {
      setScalingValue(value);
      return;
    }
    
    setScalingValue(numValue);
    
    // Reset stats ref to allow new stats to be captured from updated chart
    fullYearStatsSetRef.current = false;
    // Reset stats display to empty state, will be populated by TimeSeriesGraph callback
    setStats({
      electrical: { max: 0, min: 0, mean: 0, energy: 0 },
      thermal: { max: 0, min: 0, mean: 0, energy: 0 },
    });
  };

  // Sync scaling value when it changes via form or API
  useEffect(() => {
    const apiScalingValue = loadParameters?.componentAttributes?.loadParameters?.profileDetail?.scalingFactor;
    if (apiScalingValue !== undefined && apiScalingValue !== null) {
      const numValue = parseFloat(apiScalingValue);
      if (!isNaN(numValue) && numValue !== parseFloat(scalingValue)) {
        setScalingValue(numValue);
        // Reset stats when scaling changes so TimeSeriesGraph can provide fresh stats
        fullYearStatsSetRef.current = false;
        setStats({
          electrical: { max: 0, min: 0, mean: 0, energy: 0 },
          thermal: { max: 0, min: 0, mean: 0, energy: 0 },
        });
      }
    }
  }, [loadParameters?.componentAttributes?.loadParameters?.profileDetail?.scalingFactor]);

  // Removed local stats recalculation; TimeSeriesGraph drives stats via callback

  useEffect(() => {
    handleScalingBuildingType();
  }, [scalingValue, initialData]);

  useEffect(() => {
    const ashraeBuildingType =
      loadParameters?.componentAttributes?.loadParameters?.profileDetail
        ?.profileDataLoc;
    if (ashraeBuildingType) {
      getAshraeData(ashraeBuildingType);
    }
  }, [loadParameters?.componentAttributes?.loadParameters?.profileDetail
        ?.profileDataLoc]);
        

  return (
    <div ref={ashraeContainerRef}>
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col span={3}>
          <Select
           {...getFieldAttributes("field-6")}
            data-id="field-6"
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t('ashrae.buildingType')}
            data={buildingTypeOptions}
            value={
              loadParameters?.componentAttributes?.loadParameters?.profileDetail
                ?.profileDataLoc
            }
            required
            placeholder={t('ashrae.selectBuildingType')}
            withinPortal
            data-testid="building-type"
            {...form.getInputProps(
              "componentAttributes.loadParameters.profileDetail.profileDataLoc"
            )}
            onChange={(value) => {
              updateLoadState(
                "componentAttributes.loadParameters.profileDetail",
                "profileDataLoc",
                value
              );
            }}
            searchable
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <TextInput
           {...getFieldAttributes("field-7")}
            data-id="field-7"
            label={t('ashrae.scalingLabel')}
            type="text"
            id="electricalScale"
            placeholder="0.1 - 9.9"
            value={
              loadParameters?.componentAttributes?.loadParameters?.profileDetail
                ?.scalingFactor || 1
            }
            {...form.getInputProps(
              "componentAttributes.loadParameters.profileDetail.scalingFactor"
            )}
            onChange={(event) => {
              const {
                target: { value },
              } = { ...event };

              // If value is empty, skip regex validation and clear state
              if (value === "") {
                handleScaleChange(value);
                updateLoadState(
                  "componentAttributes.loadParameters.profileDetail",
                  "scalingFactor",
                  value
                );
                return;
              }
              
              // Allow only numbers and one decimal point
              const regex = /^\d{0,1}\.?\d{0,1}$/;
              if (!regex.test(value)) return;
              
              const numValue = parseFloat(value);
              const hasDecimal = value.includes('.');
              const endsWithDecimal = value.endsWith('.');
              
              if (isNaN(numValue)) return;
              
              // Allow intermediate values while typing:
              // - If user just typed a decimal (e.g., "0." or "5."), allow it
              // - Single digit without decimal (0-9), allow it
              // - Only validate range for complete numbers with decimal digits after the point
              
              // Allow values ending with decimal point (e.g., "0.", "5.")
              if (endsWithDecimal) {
                handleScaleChange(value);
                updateLoadState(
                  "componentAttributes.loadParameters.profileDetail",
                  "scalingFactor",
                  value
                );
                return;
              }
              
              // For single digits without decimal, allow 0-9
              if (!hasDecimal && numValue <= 9.9) {
                handleScaleChange(value);
                updateLoadState(
                  "componentAttributes.loadParameters.profileDetail",
                  "scalingFactor",
                  value
                );
                return;
              }
              
              // For complete decimal numbers, validate range 0.1 to 9.9
              if (hasDecimal && (numValue < 0.1 || numValue > 9.9)) {
                return;
              }
              
              handleScaleChange(value);
              updateLoadState(
                "componentAttributes.loadParameters.profileDetail",
                "scalingFactor",
                value
              );
            }}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-evenly",
              height: "100%",
              gap:'3%'
            }}
          >
            <se-icon size="medium" style={{ paddingBottom: "1.5%" }}>
              energy
            </se-icon>
            <div>
              <Text>{t('ashrae.max')}: {stats?.electrical?.max || 0} {displayUnit}</Text>
              <Text>{t('ashrae.min')}: {stats?.electrical?.min || 0} {displayUnit}</Text>
            </div>
            <div>
              <Text>{t('ashrae.mean')}: {stats?.electrical?.mean || 0} {displayUnit}</Text>
              <Text>{t('ashrae.energy')}: {stats?.electrical?.energy || 0} {displayUnit}h</Text>
            </div>
            <se-icon size="medium" style={{ paddingBottom: "1.5%" }}>
              thermal_loads
            </se-icon>
            <div>
              <Text>{t('ashrae.max')}: {stats?.thermal?.max || 0} {displayUnit}</Text>
              <Text>{t('ashrae.mean')}: {stats?.thermal?.mean || 0} {displayUnit}</Text>
            </div>
            <div>
              <Text>{t('ashrae.min')}: {stats?.thermal?.min || 0} {displayUnit}</Text>
              <Text>{t('ashrae.energy')}: {stats?.thermal?.energy || 0} {displayUnit}h</Text>
            </div>
          </div>
        </Grid.Col>
      </Grid>
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col span={12}>
          {displayLoader && <se-loading loading={displayLoader}></se-loading>}
          {scalingValue &&
          parseFloat(scalingValue) > 0 &&
          loadParameters?.componentAttributes?.loadParameters?.profileDetail
            ?.profileDataLoc &&
          ASHRAEDataAssest &&
          ASHRAEDataAssest?.ProfileElec?.[1]?.length ? (
            <TimeSeriesGraph
              dataAssset={ASHRAEDataAssest?.ProfileElec?.[1]}
              dataAssset2={ASHRAEDataAssest?.ProfileTherm?.[1]}
              color={"#ff6384"}
              name="Ashrae"
              componentData={componentData}
              yAxisName="W"
              embedHeight={500}
              showMinimap={true}
              minimapGap={40}
              lazyLoad={true}
              onStats={handleChartStats}
              onUnitChange={handleUnitChange}
            />
          ) : null}
        </Grid.Col>
      </Grid>
    </div>
  );
};

ASHRAELoad.propTypes = {
  componentData: PropTypes.object,
  getBuildingTypes: PropTypes.array,
  selectedBuildingType: PropTypes.string,
  displayLoader: PropTypes.bool,
  ashraeData: PropTypes.object,
  projectDetails: PropTypes.object,
  setDisplayLoader: PropTypes.func,
  form: PropTypes.object,
  loadParameters: PropTypes.object,
  updateLoadState: PropTypes.func,
};
export default ASHRAELoad;
